import { Subject, Subscription } from 'rxjs';
import { DefaultTableModel } from './TableModel';
import { ColumnDef } from './types';
import { StringDataType, NumberDataType } from './DataType';

/**
 * Row change event for real-time updates
 */
export interface RowChange<TRow> {
  type: 'upsert' | 'delete';
  row?: TRow;
  rowId?: string;
}

/**
 * Table model for RPC viewer with real-time updates
 */
export class RpcTableModel<TRow = unknown> extends DefaultTableModel<TRow> {
  private rowIdFn: (row: TRow) => string;
  private rowMap: Map<string, TRow>;
  private realtimeSubject?: Subject<RowChange<TRow>>;
  private subscription?: Subscription;
  private updateCallback?: () => void;

  constructor(
    columns: ColumnDef<TRow>[],
    initialData: TRow[],
    rowIdFn: (row: TRow) => string,
    realtimeSubject?: Subject<RowChange<TRow>>,
    onUpdate?: () => void
  ) {
    super(columns, initialData);
    this.rowIdFn = rowIdFn;
    this.rowMap = new Map();
    this.realtimeSubject = realtimeSubject;
    this.updateCallback = onUpdate;

    // Initialize row map with initial data
    initialData.forEach((row) => {
      const rowId = this.rowIdFn(row);
      this.rowMap.set(rowId, row);
    });

    // Subscribe to real-time updates
    if (this.realtimeSubject) {
      this.subscription = this.realtimeSubject.subscribe({
        next: (change) => this.handleRowChange(change),
      });
    }
  }

  private handleRowChange(change: RowChange<TRow>): void {
    if (change.type === 'upsert' && change.row) {
      const rowId = this.rowIdFn(change.row);
      const existingIndex = this.data.findIndex((r) => this.rowIdFn(r) === rowId);

      if (existingIndex >= 0) {
        // Update existing row
        this.data[existingIndex] = change.row;
      } else {
        // Add new row
        this.data.push(change.row);
      }

      this.rowMap.set(rowId, change.row);
    } else if (change.type === 'delete' && change.rowId) {
      // Remove row
      const index = this.data.findIndex((r) => this.rowIdFn(r) === change.rowId);
      if (index >= 0) {
        this.data.splice(index, 1);
      }
      this.rowMap.delete(change.rowId);
    }

    // Notify update callback
    if (this.updateCallback) {
      this.updateCallback();
    }
  }

  /**
   * Get row by ID
   */
  getRowById(rowId: string): TRow | undefined {
    return this.rowMap.get(rowId);
  }

  /**
   * Clean up subscription
   */
  dispose(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}

/**
 * Helper to create columns with appropriate data types
 */
export function createRpcColumn<TRow>(
  id: string,
  header: string,
  accessor: (row: TRow) => unknown,
  width: number = 150,
  options?: {
    hidden?: boolean;
    dataType?: 'string' | 'number';
    sortable?: boolean;
  }
): ColumnDef<TRow> {
  const dataType = options?.dataType === 'number' ? new NumberDataType() : new StringDataType();

  return {
    id,
    header,
    width,
    accessor,
    dataType,
    sortable: options?.sortable !== false,
    visible: !options?.hidden,
  };
}
